import type {
  AssignSeatTwoBody,
  ChangePremiumEndBody,
  DataRoomAccess,
  EnablePremiumBody,
  PremiumHistoryRow,
  RevokePremiumBody,
} from '@/lib/types/data-room';
import { apiClient } from './client';

/**
 * Data-room access for one startup (v1.2, Backend design §13b). Cross-feature:
 * Team Access, Billing and the admin data-room panel all read the same view,
 * so the three screens can never disagree about seats or end dates.
 *
 * @param startupId - the startup entity
 * @param segment - the sub-resource (`access`, `seats/2`, `premium`, `premium/revoke`, `premium/history`)
 */
function accessPath(startupId: string, segment: string): string {
  return `/startups/${startupId}/data-room/${segment}`;
}

/**
 * What the startup holds now, its seats, and its Issue Report history.
 * Startup Admins only; a Super Admin reads the same resource.
 *
 * @param startupId - the startup entity
 */
export async function getDataRoomAccess(startupId: string): Promise<DataRoomAccess> {
  const { data } = await apiClient.get<DataRoomAccess>(accessPath(startupId, 'access'));
  return data;
}

/**
 * Gives or moves seat 2; only the seat 1 holder may (FR-139).
 *
 * @param startupId - the startup entity
 * @param body - the new holder and the holder the caller saw
 * @returns the refreshed access view
 */
export async function assignSeatTwo(startupId: string, body: AssignSeatTwoBody): Promise<DataRoomAccess> {
  const { data } = await apiClient.put<DataRoomAccess>(accessPath(startupId, 'seats/2'), body);
  return data;
}

/**
 * Empties seat 2; only the seat 1 holder may (FR-139). Seat 1 has no release.
 *
 * @param startupId - the startup entity
 * @param expectedHolderUserId - who the caller saw in seat 2; a concurrent change is refused
 * @returns the refreshed access view
 */
export async function releaseSeatTwo(startupId: string, expectedHolderUserId: string): Promise<DataRoomAccess> {
  const { data } = await apiClient.delete<DataRoomAccess>(accessPath(startupId, 'seats/2'), {
    params: { expectedHolderUserId },
  });
  return data;
}

/**
 * Switches on SR NEXT Premium; MNR admins only (FR-133). With `restart: true`
 * it starts a new period while Premium is on (MYN-258).
 *
 * @param startupId - the startup entity
 * @param body - the period, the seat 1 holder and, for a new period, `restart`
 * @returns the refreshed access view
 */
export async function enablePremium(startupId: string, body: EnablePremiumBody): Promise<DataRoomAccess> {
  const { data } = await apiClient.post<DataRoomAccess>(accessPath(startupId, 'premium'), body);
  return data;
}

/**
 * Moves the end date of the active Premium period; MNR admins only (MYN-258).
 *
 * @param startupId - the startup entity
 * @param body - the new end instant
 * @returns the refreshed access view
 */
export async function changePremiumEnd(startupId: string, body: ChangePremiumEndBody): Promise<DataRoomAccess> {
  const { data } = await apiClient.patch<DataRoomAccess>(accessPath(startupId, 'premium'), body);
  return data;
}

/**
 * Revokes the active Premium period now, with a reason; MNR admins only
 * (MYN-258). The data room is frozen; its documents are kept.
 *
 * @param startupId - the startup entity
 * @param body - the reason, recorded in the history
 * @returns the refreshed access view
 */
export async function revokePremium(startupId: string, body: RevokePremiumBody): Promise<DataRoomAccess> {
  const { data } = await apiClient.post<DataRoomAccess>(accessPath(startupId, 'premium/revoke'), body);
  return data;
}

/**
 * Every Premium period the startup has had, newest first; MNR admins only (MYN-258).
 *
 * @param startupId - the startup entity
 */
export async function getPremiumHistory(startupId: string): Promise<PremiumHistoryRow[]> {
  const { data } = await apiClient.get<PremiumHistoryRow[]>(accessPath(startupId, 'premium/history'));
  return data;
}
